import type { ConversationSummaryEvent } from '@/lib/streamingClient';
import type { LiveSessionData } from '@/types/session';
import type { RegenTriggerHint, SummaryGeneration, SummaryGenerationEcho } from '@/types/summaryGeneration';

export const NO_SUMMARY_GENERATION: SummaryGeneration = { generationId: null, templateId: null };

/** Generation behind a websocket summary; unpaired (no id) when the server sent none. */
export const summaryGenerationFromEvent = (
  event: Pick<ConversationSummaryEvent, 'generation_id' | 'template_id'>
): SummaryGeneration => ({
  generationId: event.generation_id ?? null,
  templateId: event.template_id ?? null,
});

/** Generation behind an HTTP summary response; falls back to the requested template id. */
export const summaryGenerationFromHttp = (
  data: SummaryGenerationEcho,
  selectedTemplateId: number | null
): SummaryGeneration => ({
  generationId: data.generation_id ?? null,
  templateId: data.template_id ?? selectedTemplateId,
});

/**
 * Generation echo for a loaded session, matching the same user_summary >
 * model_summary priority used to pick which text is displayed (the audio-record
 * fallbacks predate generation tracking, so they fall through to no id/reset).
 */
export const summaryEchoForLoadedSession = (
  data: Pick<LiveSessionData, 'user_summary' | 'model_summary'>
): SummaryGenerationEcho => {
  if (data.user_summary?.summary_text) {
    return { generation_id: data.user_summary.generation_id, template_id: data.user_summary.selected_template_id };
  }
  if (data.model_summary?.summary_text) {
    return { generation_id: data.model_summary.generation_id, template_id: data.model_summary.selected_template_id };
  }
  return {};
};

/**
 * Generation echo for a restored localStorage draft: the generation it was edited from. A
 * draft of an older generation (or one stored before drafts carried an id) then saves
 * unpaired instead of pairing with whichever generation is current now.
 */
export const summaryEchoForDraft = (draft: Partial<SummaryGeneration>): SummaryGenerationEcho => ({
  generation_id: draft.generationId ?? null,
  template_id: draft.templateId ?? null,
});

/** Regeneration hint for the next generate request, from what is on screen now. */
export const regenTriggerHint = (
  hasSummary: boolean,
  templateId: number,
  lastGeneration: SummaryGeneration
): RegenTriggerHint => {
  if (!hasSummary) return 'initial';
  const templateChanged = lastGeneration.templateId != null && lastGeneration.templateId !== templateId;
  return templateChanged ? 'template' : 'retry';
};
